// One shooter's Megalink shots on one Lag, laid out series by series for the
// «shooterShots» drawer on the convention page — Tilleggsmodul (C, shooting).
// Input is the /api/megalinkshots/live response (state 'open' or 'replay'),
// which carries every shot of the Lag with lane, countStatus and the series
// slot (cellTargetIndex) the pipeline scored it into.

import {
    groupShots, seriesTotal, targetFor,
} from './pistolTargets';

export const SIGHTER_LABEL = 'Prøve';
export const EXTRA_LABEL = 'Ekstra';
export const REPLACED_LABEL = 'Erstattet (omskyting)';

const CENTIS_PER_DAY = 24 * 60 * 60 * 100;

function hasCentis(shot) {
    return !!shot && Number.isInteger(shot.megalinkCentis);
}

// Split time between two shots from Megalink's own time of day (1/100 s),
// wrapping at midnight. null when either shot lacks it (legacy-mld reader).
export function splitCentis(prev, shot) {
    if (!hasCentis(prev) || !hasCentis(shot)) return null;
    let diff = shot.megalinkCentis - prev.megalinkCentis;
    if (diff < 0) diff += CENTIS_PER_DAY;
    return diff;
}

// «1,23» — seconds with two decimals and a Norwegian decimal comma.
export function formatSeconds(centis) {
    if (!Number.isInteger(centis)) return '';
    return (centis / 100).toFixed(2).replace('.', ',');
}

// Copies of the shots (never the shared live rows) in the order given, each
// with `split`: centiseconds since the previous shot of the list, null on the
// first shot or without Megalink time. `duration` is first → last shot.
export function withSplits(shots) {
    const list = Array.isArray(shots) ? shots : [];
    const out = list.map((shot, i) => ({ ...shot, split: i > 0 ? splitCentis(list[i - 1], shot) : null }));
    const duration = list.length > 1 ? splitCentis(list[0], list[list.length - 1]) : null;
    return { shots: out, duration };
}

function laneOf(live, participantId) {
    const list = live && live.stage && Array.isArray(live.stage.participants) ? live.stage.participants : [];
    const entry = list.find(p => p && parseInt(p.id, 10) === parseInt(participantId, 10));
    return entry ? entry.lane : null;
}

/**
 * @param live          /api/megalinkshots/live response data
 * @param participantId user.id of the shooter
 * @param labels        series labels from conventionResultStore.getParticipantFields(stage)
 * @returns { lane, stageType, layout, shotCount, total, series: [{ key, kind, label, target, shots, total, duration }] }
 *          series[].shots[].split = 1/100 s since the previous shot of the card (null on
 *          the first shot / without Megalink time); duration = first → last shot.
 *          or null when the shooter is not on the Lag / nothing is loaded.
 *          kind: 'sighters' | 'series' | 'extras' | 'replaced'. Sighters, extras
 *          and replaced (a series re-shot after a malfunction; the re-shoot sits
 *          in the series' own card) are present only when the shooter has such
 *          shots; program series always.
 */
export function buildShooterSeries({ live, participantId, labels = [] }) {
    const lane = laneOf(live, participantId);
    if (lane === null || lane === undefined) return null;
    const stageType = (live.stage && live.stage.type) || '';
    const layout = live.stage && live.stage.layout ? live.stage.layout : null;
    const mine = (Array.isArray(live.shots) ? live.shots : []).filter(s => s && s.lane === lane);
    const g = groupShots(mine, layout);
    const series = [];
    const card = (key, kind, label, target, shots) => ({
        key, kind, label, target, total: seriesTotal(shots), ...withSplits(shots),
    });
    if (g.sighters.length > 0) {
        series.push(card('sighters', 'sighters', SIGHTER_LABEL, targetFor(stageType, 0), g.sighters));
    }
    g.series.forEach((shots, k) => {
        const label = Array.isArray(labels) && labels[k] !== undefined && labels[k] !== null && labels[k] !== ''
            ? String(labels[k])
            : String(k + 1);
        series.push(card(`series-${k}`, 'series', label, targetFor(stageType, k), shots));
    });
    if (g.replaced.length > 0) {
        series.push(card('replaced', 'replaced', REPLACED_LABEL, targetFor(stageType, Math.max(g.series.length - 1, 0)), g.replaced));
    }
    if (g.extras.length > 0) {
        series.push(card('extras', 'extras', EXTRA_LABEL, targetFor(stageType, Math.max(g.series.length - 1, 0)), g.extras));
    }
    const counted = g.series.reduce((all, list) => all.concat(list), []);
    return {
        lane,
        stageType,
        layout,
        shotCount: mine.length,
        total: seriesTotal(counted),
        series,
    };
}
